import { useCallback, useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { ImageIcon } from "lucide-react";

type Props = {
  beforeSrc: string;
  afterSrc: string;
  alt: string;
  label: string;
  caption: string;
};

const WIDTHS = [400, 800, 1200, 1600];
const SIZES = "(max-width: 640px) 92vw, (max-width: 1024px) 90vw, 720px";

const buildSrcSet = (src: string, ext: "webp" | "jpg" | "avif") => {
  const stem = src.replace(/\.jpg$/, "");
  return WIDTHS.map((w) => `${stem}-${w}w.${ext} ${w}w`).join(", ");
};

const DEFAULT_POS = 28;
const DEMO_FROM = 62;
const DEMO_DURATION = 900;
const MIN_POS = 0;
const MAX_POS = 100;

// Module-scoped so the demo animation fires at most once per page load,
// even though <AnimatePresence key={active}> remounts this component on
// every industry-tab swap.
let demoPlayed = false;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const Fallback = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[hsl(220,6%,11%)]">
    <ImageIcon className="h-7 w-7 text-cream/40" strokeWidth={1.5} />
    <span className="text-[11px] text-cream/40 font-mono uppercase tracking-wider">Preview coming soon</span>
  </div>
);

const BeforeAfterCard = ({ beforeSrc, afterSrc, alt, label, caption }: Props) => {
  const prefersReducedMotion = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  const inView = useInView(trackRef, { amount: 0.5, margin: "0px 0px -10% 0px", once: true });
  const userTouchedRef = useRef(false);
  const [pos, setPos] = useState(DEFAULT_POS);
  const [dragging, setDragging] = useState(false);
  const [beforeFailed, setBeforeFailed] = useState(false);
  const [afterFailed, setAfterFailed] = useState(false);

  // One-time demo: ease from DEMO_FROM down to DEFAULT_POS so users see
  // the handle move and understand it's draggable. Gated via module-scoped
  // demoPlayed so it doesn't re-fire each time AnimatePresence remounts
  // this card on tab swap.
  useEffect(() => {
    if (!inView || prefersReducedMotion || userTouchedRef.current || demoPlayed) return;
    demoPlayed = true;
    setPos(DEMO_FROM);
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      if (userTouchedRef.current) return;
      const t = Math.min(1, (now - start) / DEMO_DURATION);
      setPos(DEMO_FROM + (DEFAULT_POS - DEMO_FROM) * easeOutCubic(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, prefersReducedMotion]);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    userTouchedRef.current = true;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(MAX_POS, Math.max(MIN_POS, raw)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => updateFromClientX(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, updateFromClientX]);

  const onPointerDown = (e: React.PointerEvent) => {
    setDragging(true);
    updateFromClientX(e.clientX);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    let handled = true;
    if (e.key === "ArrowLeft") setPos((p) => Math.max(MIN_POS, p - step));
    else if (e.key === "ArrowRight") setPos((p) => Math.min(MAX_POS, p + step));
    else if (e.key === "Home") setPos(MIN_POS);
    else if (e.key === "End") setPos(MAX_POS);
    else handled = false;
    if (handled) {
      userTouchedRef.current = true;
      e.preventDefault();
    }
  };

  return (
    <div className="relative rounded-xl overflow-hidden border border-cream/10 bg-[hsl(220,5%,12%)] shadow-xl shadow-black/30">
      <div className="bg-[hsl(220,5%,16%)] border-b border-cream/10 px-3 py-2.5 flex items-center gap-2.5">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(5,70%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(38,80%,55%)]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[hsl(130,60%,50%)]" />
        </div>
        <div className="flex-1 mx-2 bg-[hsl(220,5%,12%)] rounded-md px-3 py-1 flex items-center gap-2 min-w-0">
          <div className="h-2 w-2 rounded-full bg-sage-bright shrink-0 shadow-[0_0_6px_hsl(var(--sage-bright)/0.6)]" />
          <span className="text-[10px] text-cream/70 font-semibold tracking-wide truncate">{label}</span>
        </div>
        <span className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full bg-sage-bright/15 border border-sage-bright/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-sage-bright">
          Drag to compare
        </span>
      </div>

      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        className={`relative w-full aspect-[16/10] bg-cream/5 overflow-hidden select-none touch-none ${dragging ? "cursor-grabbing" : "cursor-ew-resize"}`}
      >
        {afterFailed ? (
          <Fallback />
        ) : (
          <picture>
            <source type="image/webp" srcSet={buildSrcSet(afterSrc, "webp")} sizes={SIZES} />
            <source type="image/jpeg" srcSet={buildSrcSet(afterSrc, "jpg")} sizes={SIZES} />
            <img
              src={afterSrc}
              alt={`${alt} — after`}
              width={800}
              height={500}
              loading="lazy"
              decoding="async"
              draggable={false}
              onError={() => setAfterFailed(true)}
              className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
            />
          </picture>
        )}

        <div
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          {beforeFailed ? (
            <Fallback />
          ) : (
            <picture>
              <source type="image/webp" srcSet={buildSrcSet(beforeSrc, "webp")} sizes={SIZES} />
              <source type="image/jpeg" srcSet={buildSrcSet(beforeSrc, "jpg")} sizes={SIZES} />
              <img
                src={beforeSrc}
                alt={`${alt} — before`}
                width={800}
                height={500}
                loading="lazy"
                decoding="async"
                draggable={false}
                onError={() => setBeforeFailed(true)}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </picture>
          )}
        </div>

        <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-[0.15em] rounded px-1.5 py-0.5 bg-[hsl(5,70%,55%)]/90 text-cream backdrop-blur-sm pointer-events-none">
          Before
        </span>
        <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-[0.15em] rounded px-1.5 py-0.5 bg-sage-bright/90 text-charcoal backdrop-blur-sm pointer-events-none">
          After
        </span>

        <div
          className="absolute top-0 bottom-0 w-px bg-cream/90 shadow-[0_0_8px_hsl(var(--cream)/0.5)] pointer-events-none"
          style={{ left: `${pos}%` }}
        />
        <button
          type="button"
          role="slider"
          aria-label="Reveal before / after"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          onKeyDown={onKeyDown}
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full bg-cream/95 border-2 border-sage-bright shadow-lg shadow-black/40 flex items-center justify-center text-charcoal transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-bright focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal ${dragging ? "scale-110 cursor-grabbing" : "cursor-grab"}`}
          style={{ left: `${pos}%` }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9 6 L4 12 L9 18" />
            <path d="M15 6 L20 12 L15 18" />
          </svg>
        </button>
      </div>

      <div className="px-4 py-4 border-t border-cream/10">
        <p className="text-sm text-cream/80 leading-snug md:text-base">{caption}</p>
      </div>
    </div>
  );
};

export default BeforeAfterCard;
